import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalController, NavController } from '@ionic/angular';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { ONGOING_LOGIN_SESSION_INFO } from 'src/app/pages/authentication/home/home.page';
import * as moment from 'moment';
import { CountryISO } from 'ngx-intl-tel-input';
import { InformationModalPage } from 'src/app/pages/authentication/site-code-entry/information-modal/information-modal';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { AlertService } from '../../../services/alert.service';
import { AuthService } from '../../../services/auth.service';
import { LoadingService } from '../../../services/loading.service';
import { Gender, GENDERS } from '../../../types/Gender';
import { CLINICIAN, HEALTH_CARE_PROVIDER, INPUT_DATE_FORMAT } from '../../../utils/constants';
import { ValidationUtil, Validator } from '../../../utils/validation';


export enum RegisterFieldsPage1 {
  FIRST = 'first',
  LAST = 'last',
  DOB = 'dob',
  GENDER = 'gender'
}

export enum RegisterFieldsPage2 {
  PHONE = 'phone',
  SITE = 'site',
}


type RegisterFormConfig = {
  [index in RegisterFieldsPage1 | RegisterFieldsPage2]: [string, Validator[]] | FormControl
};

@Component({
  selector: 'app-register',
  templateUrl: './register.page.html',
  styleUrls: ['./register.page.scss', '../authentication.scss']
})
export class RegisterPage implements OnInit, OnDestroy {

  title = 'Register';
  registerForm: FormGroup;

  maxDateStr: string;
  phoneNumber: string = '';
  normalizedPhoneNumber!: string;
  phone: any;
  healthCareProvider: string = HEALTH_CARE_PROVIDER;
  clinician: string = CLINICIAN;
  genders: Gender[] = GENDERS;
  selectedCountryISO = CountryISO.Australia;
  private locale: string = '';
  private formSubmitted = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public alertProvider: AlertService,
    private docTitle: Title,
    private navCtrl: NavController,
    private formBuilder: FormBuilder,
    private modalCtrl: ModalController,
    private loadingProvider: LoadingService,
    private amplifyProvider: AuthService,
    private analyticsService: AnalyticsService,
    private ngZone: NgZone,
  ) {
    this.analyticsService.tagRoute('unauthenticated/register');
    this.maxDateStr = moment().startOf('day').format(INPUT_DATE_FORMAT);

    const formConfig: RegisterFormConfig = {
      first: ['', [ValidationUtil.isRequired]],
      last: ['', [ValidationUtil.isRequired]],
      dob: [
        '',
        [ValidationUtil.isRequired, ValidationUtil.dateInPast]
      ],
      gender: ['', [ValidationUtil.isRequired]],
      phone: ['', [ValidationUtil.isRequired]],
      site: ['', [ValidationUtil.isRequired]]
    };

    this.docTitle.setTitle(this.title);
    this.registerForm = this.formBuilder.group(formConfig);
  }

  async openInformationModal() {
    const modal = await this.modalCtrl.create({
      component: InformationModalPage,
    });
    await modal.present();
  }

  ngOnInit() {
    this.selectedCountryISO = CountryISO.Australia;
    this.route.queryParamMap.subscribe(params => {
      const siteCode = params.get('siteCode');
      const locale = params.get('locale');
      this.locale = locale || '';
      this.restoreAndSaveSession(siteCode, locale);
      if (locale) {
        const countryCode = this.getCountryIsoFromLocale(locale);
        const foundCountryCode = Object.values(CountryISO).find(cc => cc === countryCode?.toLowerCase());
        if (foundCountryCode) {
          this.selectedCountryISO = foundCountryCode;
        }
      }
      if (siteCode) {
        this.registerForm.controls['site'].setValue(siteCode);
      }
    });
  }

  private async restoreAndSaveSession(siteCode: string | null, locale: string | null) {
    const stored = JSON.parse(
      (await SecureStoragePlugin.get({ key: ONGOING_LOGIN_SESSION_INFO }).catch(() => {}))?.value ?? '{}'
    ) as { page?: string; siteCode?: string; formData?: { first?: string; last?: string; dob?: string; gender?: string; phone?: string } };

    try { SecureStoragePlugin.set({ key: ONGOING_LOGIN_SESSION_INFO, value: JSON.stringify({ page: 'register', siteCode: siteCode || '', locale: locale || '' }) }).catch(() => {}); } catch (e) {}

    if (stored.page === 'register' && stored.siteCode === (siteCode || '') && stored.formData) {
      const patch: Partial<{ first: string; last: string; dob: string; gender: string }> = {};
      if (stored.formData.first) patch.first = stored.formData.first;
      if (stored.formData.last) patch.last = stored.formData.last;
      if (stored.formData.dob) patch.dob = stored.formData.dob;
      if (stored.formData.gender) patch.gender = stored.formData.gender;
      if (Object.keys(patch).length) {
        this.ngZone.run(() => this.registerForm.patchValue(patch));
      }
    }
  }

  ngOnDestroy() {
    if (this.formSubmitted) return;
    const formData = this.registerForm.value;
    try {
      SecureStoragePlugin.set({
        key: ONGOING_LOGIN_SESSION_INFO,
        value: JSON.stringify({
          page: 'register',
          siteCode: formData.site || '',
          locale: this.locale,
          formData: {
            first: formData.first || '',
            last: formData.last || '',
            dob: formData.dob || '',
            gender: formData.gender || '',
          }
        })
      }).catch(() => {});
    } catch (e) {}
  }

  private getCountryIsoFromLocale(locale: string): string | null {
    try {
      const intlLocale = new Intl.Locale(locale);
      return intlLocale.region || null;
    } catch (error) {
      return null;
    }
  }

  submitForm(): void {
    this.normalizedPhoneNumber = this.phoneNumber ?? '';
    const singleUsePassword = this.amplifyProvider.generateNewPassword();
    this.loadingProvider.loadWhile(async (stopLoading) => {
      try {
        const formData = this.registerForm.value;
        const birthdate = moment(formData.dob).format(INPUT_DATE_FORMAT);
        const registerData = {
          phone_number: this.normalizedPhoneNumber,
          password: singleUsePassword,
          given_name: formData.first.trim(),
          family_name: formData.last.trim(),
          birthdate,
          gender: formData.gender,
          'custom:sitecode': formData.site.toLowerCase().trim()
        };
        await this.amplifyProvider.registerUser(registerData);
        this.formSubmitted = true;
        this.navCtrl.navigateRoot('/unauthenticated/verifysignup', { queryParams: { phone: this.normalizedPhoneNumber } });
      } catch (error) {
        this.handleError(error);
      } finally {
        stopLoading();
      }
    });
  }

  handleError(error: any) {
    if (this.amplifyProvider.isUsernameExistsException(error)) {
      this.handleUsernameExistsException(error);
    } else if (this.amplifyProvider.isUserLambdaValidationException(error)) {
      this.handleValidationException(error);
    } else {
      this.alertProvider.showAmplifyAuthError(error);
    }
  }

  handleUsernameExistsException(error: any) {
    console.error(error);
    const registeredPhoneNumber = this.registerForm.controls['phone'].value;
    this.alertProvider.confirm(
      `This mobile number is already registered`,
      `Check your number and try again. Alternative contact support@cardihab.com`,
      `Go to Login`,
      () => {
        this.router.navigate(['unauthenticated', 'login'], {
          queryParams: { registeredPhoneNumber }
        });
      }
    );
  }

  handleValidationException(error: any) {
    console.error(error);
    this.alertProvider.confirm(
      `Provider site not found`,
      `Please ask your ${this.healthCareProvider} or ${this.clinician} for this information.`
    );
  }

  cancel() {
    if (this.registerForm.dirty) {
      this.showDiscardConfirmation();
    } else {
      this.analyticsService.tagButtonPress('registration', 'cancel');
      this.router.navigate(['unauthenticated', 'home']);
    }
  }

  showDiscardConfirmation(): void {
    this.alertProvider.confirmDiscard(
      'Discard unsaved changes?',
      'Do you want to discard unsaved changes?',
      'Discard',
      () => {
        this.analyticsService.tagButtonPress('registration', 'cancel');
        this.router.navigate(['unauthenticated', 'home']);
      }
    );
  }

}
